import React, {memo, useEffect, useMemo, useRef, useState} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {
    formatProductPickerPrice,
    formatStockQuantity,
    getProductImageUrl,
    hasStockQuantity,
} from './modeStorage';
import * as S from './styles';
import {ProductPickerItem} from './types';

const TILE_MIN_WIDTH = 148;
const TILE_GAP = 12;
const TILE_ESTIMATE_HEIGHT = 220;

interface TileCardProps {
    product: ProductPickerItem;
    selectedQty: number;
    showImages: boolean;
    onPick: (product: ProductPickerItem) => void;
}

const TileCard = memo(({product, selectedQty, showImages, onPick}: TileCardProps) => {
    const imageUrl = showImages ? getProductImageUrl(product) : undefined;

    return (
        <S.TouchTile
            type="button"
            $selected={selectedQty > 0}
            onClick={() => onPick(product)}
        >
            {selectedQty > 0 && (
                <S.TouchTileQtyBadge>
                    {selectedQty}
                </S.TouchTileQtyBadge>
            )}
            <S.TouchTileImage>
                {showImages && imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    showImages ? 'Нет фото' : product.name.slice(0, 1).toUpperCase() || '•'
                )}
                <S.TouchTilePriceOverlay>
                    <span>{formatProductPickerPrice(product)}</span>
                    {hasStockQuantity(product) && (
                        <S.TouchTileStock>
                            {formatStockQuantity(product)}
                        </S.TouchTileStock>
                    )}
                </S.TouchTilePriceOverlay>
            </S.TouchTileImage>
            <S.TouchTileInfo>
                <S.TouchTileName>{product.name}</S.TouchTileName>
                {hasStockQuantity(product) && (
                    <S.TouchTileStockMeta>
                        В наличии: {formatStockQuantity(product)}
                    </S.TouchTileStockMeta>
                )}
            </S.TouchTileInfo>
        </S.TouchTile>
    );
});

TileCard.displayName = 'TileCard';

interface Props {
    products: ProductPickerItem[];
    qtyByProductId: Map<number, number>;
    showImages?: boolean;
    singlePick?: boolean;
    loading?: boolean;
    onPick: (product: ProductPickerItem) => void;
}

const VirtualTilesGrid = ({
    products,
    qtyByProductId,
    showImages = true,
    singlePick,
    loading,
    onPick,
}: Props) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const el = parentRef.current;
        if (!el) return;

        const update = () => setWidth(el.clientWidth);
        update();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const columns = useMemo(() => {
        if (width <= 0) return 1;
        return Math.max(1, Math.floor((width + TILE_GAP) / (TILE_MIN_WIDTH + TILE_GAP)));
    }, [width]);

    const rowCount = products.length === 0 ? 0 : Math.ceil(products.length / columns);

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => parentRef.current,
        estimateSize: () => TILE_ESTIMATE_HEIGHT + TILE_GAP,
        overscan: 4,
    });

    if (products.length === 0) {
        return (
            <S.VirtualTilesScrollRoot ref={parentRef}>
                <S.EmptyState>
                    {loading ? 'Загрузка…' : 'В этой категории ничего нет'}
                </S.EmptyState>
            </S.VirtualTilesScrollRoot>
        );
    }

    return (
        <S.VirtualTilesScrollRoot ref={parentRef}>
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                    const startIndex = virtualRow.index * columns;
                    const rowProducts = products.slice(startIndex, startIndex + columns);

                    return (
                        <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`,
                                display: 'grid',
                                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                                gap: TILE_GAP,
                                paddingBottom: TILE_GAP,
                                boxSizing: 'border-box',
                            }}
                        >
                            {rowProducts.map((product) => (
                                <TileCard
                                    key={product.id}
                                    product={product}
                                    showImages={showImages}
                                    selectedQty={singlePick ? 0 : (qtyByProductId.get(product.id) || 0)}
                                    onPick={onPick}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>
        </S.VirtualTilesScrollRoot>
    );
};

export default VirtualTilesGrid;
